/* ADD FILE */
/* UNTAPPED - FABRICS PRICE PER METRE */
import { onAddElement } from './stripes-utils.js';
const { FABRIC_UNIT_FACTOR, LENGTH_QUANTITY_LABEL } = await import(window.STRIPES_CONSTANTS_URL);

export function formatFabricQuantity(value) {
  const decimal = value % 1;
  if (decimal === 0) {   
    return value < 10
      ? value.toFixed(1)                           // e.g. 5 → "5.0"
      : value.toFixed(0);                          // e.g. 100 → "100"
  }
  
  return value.toFixed(1);                         // fallback for other decimals
}

/**
 * Add a sibling quantity input to an existing quantity input.
 * 
 * The exist input is the "real" one that holds the true quantity in the same units as the underlying 
 * product (eg half-metres). Kalles interacts with the real input to causes updates.  It also js that 
 * writes to it to enforce integer values.  Clicking the +/- buttons also updates it.  
 * 
 * The new input is the "display" input, which is the real value / the conversion factor.
 * 
 * Hide the real one and show the new one. Add listeners, with sync flag protection to av`oid infinite loops, 
 * to copy values between them with conversion.
 * 
 * Minimums. maximums and steps are equally converted.
 * 
 * Run this against all quantity inputs on page load.  This does not unfortuneately pick up the cart drawer.
 * Although the we can update that when the drawer opens, the contents of the cart are actually recreated after 
 * a quantity is edited, so with the cart it is better to modifiy the actual html to achieve the same result as
 * here. 
 */
function upgradeQuantityInput(real) {
  console.log("Upgrading quantity");

  const PROCESSED = 'data-length-upgraded'; 
  if (!real || real.hasAttribute(PROCESSED)) return;

  real.setAttribute(PROCESSED, '1');

  let syncing = false; // ⬅ move inside

  // --- Clone the real input to preserve styling & structure
  const display = real.cloneNode(true);

  display.removeAttribute('name');
  display.removeAttribute('id');
  display.removeAttribute('data-cart-quantity');

  display.step = '0.5';
  display.min = '1';

  display.classList.add('display-quantity');
  display.classList.remove('real-quantity');

  // Domain rule
  real.min = String(FABRIC_UNIT_FACTOR);
  if (Number(real.value) < FABRIC_UNIT_FACTOR) {
    real.value = String(FABRIC_UNIT_FACTOR);
  }

  const stored = Number(real.value);
  console.log("SETTING THE DISPLAY VALUE: ", stored);
  display.value = Number.isFinite(stored)
    ? formatFabricQuantity( stored / FABRIC_UNIT_FACTOR )
    : '';

  real.style.display = 'none';
  real.classList.add('real-quantity');

  real.insertAdjacentElement('afterend', display);

  addQuantityLabel(real, LENGTH_QUANTITY_LABEL);

  // ✅ Tell CSS we are ready
  markQuantityReady(real);

  // real → display
  real.addEventListener('change', () => {
    if (syncing) return;
    syncing = true;

    let v = Number(real.value);
    if (!Number.isFinite(v) || v < FABRIC_UNIT_FACTOR) {
      v = FABRIC_UNIT_FACTOR;
      real.value = formatFabricQuantity(v);
    }

    display.value = formatFabricQuantity(v / FABRIC_UNIT_FACTOR);

    syncing = false;
  });

  // Event handling is slightly different betweem add-to-cart and modify in cart.
  // In add-to-cart the display value must always copy to real so that when the Add to Cart
  // button is clicked the correct value is there.  So we listen on input.
  // 
  // In the cart you do not want every edit to cause an update to the cart, but wait for an enter.  
  // So we listen on change, and configure enter correctly.   
  const isCartQuantity = real.name === 'updates[]';
  const eventToListenOn = isCartQuantity? 'change' : 'input';

  // display → real
  display.addEventListener(eventToListenOn, () => {
    if (syncing) return;

    const metres = Number(display.value);
    if (!Number.isFinite(metres)) return;

    syncing = true;
    real.value = Math.round(metres * FABRIC_UNIT_FACTOR);
    real.dispatchEvent(new Event('change', { bubbles: true }));
    syncing = false;
  });

  if (isCartQuantity) {
    display.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        display.blur();
      }
    });
  }
}

function addQuantityLabel(realInput, text) {
  if (!(realInput instanceof Element)) return;

  // Don't add the label in the sticky ATC bar — space is too tight
  if (realInput.closest('hdt-sticky-btn-atc')) return;

  // 1️⃣ Find the Kalles quantity wrapper from the real input
  let insertionPoint = realInput.closest('hdt-buy-buttons');
  if (!insertionPoint) insertionPoint = realInput.closest('.hdt-product-info__item');
  if (!insertionPoint) return;

  // 2️⃣ Prevent duplicates (idempotent)
  if (
    insertionPoint.previousElementSibling?.classList.contains(
      'length-quantity-note'
    )
  ) {
    return;
  }

  // 3️⃣ Create the note
  const note = document.createElement('div');
  note.className = 'length-quantity-note';
  note.textContent = text;

  // 4️⃣ Insert above quantity
  insertionPoint.insertAdjacentElement('beforebegin', note);
}

// Quantities to add an item to the cart
onAddElement({
  root: document.body,
  selector: '.is-sold-by-length .hdt-quantity__input',
  onNode: upgradeQuantityInput
});

// Quantities to modify an item in the cart
onAddElement({
  root: document.body,
  selector: '.hdt-cart-item__quantity.is-sold-by-length input[name="updates[]"]',
  onNode: upgradeQuantityInput
});

function markQuantityReady(realInput) {
  const wrapper = realInput.closest('hdt-quantity-wrapp');
  if (!wrapper) return;

  wrapper.classList.add('quantity--ready');
}
